import { useEffect } from 'react';
import { useStore } from '../store';
import { useShortcutModifier } from '../hooks/useShortcutModifier';

interface Shortcut {
  keys: string[];
  description: string;
}

interface ShortcutGroup {
  heading: string;
  shortcuts: Shortcut[];
}

export function HelpDialog() {
  const open = useStore((s) => s.helpOpen);
  const setOpen = useStore((s) => s.setHelpOpen);
  const mod = useShortcutModifier();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, setOpen]);

  if (!open) return null;

  const opt = '⌥';
  const shift = '⇧';
  const groups: ShortcutGroup[] = [
    {
      heading: 'App',
      shortcuts: [
        { keys: [`${mod}+1`], description: 'Body editor' },
        { keys: [`${mod}+2`], description: 'Headers' },
        { keys: [`${mod}+3`], description: 'Captures' },
        { keys: [`${mod}+B`], description: 'Payload library' },
        { keys: [`${mod}+K`], description: 'Quick actions: search actions, payloads and shares' },
        { keys: [`${mod}+S`], description: 'Save (update the open share, or create one)' },
        { keys: [`${mod}+${shift}+S`], description: 'Save as new share' },
        { keys: ['?'], description: 'This help dialog' },
        { keys: ['Esc'], description: 'Close dialogs' },
      ],
    },
    {
      heading: 'Editor (Monaco)',
      shortcuts: [
        { keys: [`${mod}+${shift}+P`, 'F1'], description: 'Command palette' },
        { keys: [`${mod}+D`], description: 'Add next-occurrence selection' },
        { keys: [`${mod}+L`], description: 'Select line' },
        { keys: [`${mod}+/`], description: 'Toggle line comment' },
        { keys: [`${opt}+↑`, `${opt}+↓`], description: 'Move line up / down' },
        { keys: [`${opt}+${shift}+↑`, `${opt}+${shift}+↓`], description: 'Copy line up / down' },
        { keys: [`${mod}+${shift}+K`], description: 'Delete line' },
        { keys: [`${mod}+F`], description: 'Find' },
        { keys: [`${mod}+H`], description: 'Replace' },
        { keys: ['right-click'], description: 'Encode / decode menu (xss.cool)' },
      ],
    },
  ];

  return (
    <div
      className="help-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Keyboard shortcuts"
      onClick={() => setOpen(false)}
    >
      <div className="help-dialog" onClick={(e) => e.stopPropagation()}>
        <header className="help-header">
          <strong>Keyboard shortcuts</strong>
          <button onClick={() => setOpen(false)} aria-label="Close" className="help-close">
            ×
          </button>
        </header>
        {groups.map((g) => (
          <section key={g.heading} className="help-group">
            <h4 className="help-group-heading">{g.heading}</h4>
            <ul className="help-list">
              {g.shortcuts.map((s) => (
                <li key={s.description}>
                  <span className="help-keys">
                    {s.keys.map((k) => (
                      <kbd key={k}>{k}</kbd>
                    ))}
                  </span>
                  <span>{s.description}</span>
                </li>
              ))}
            </ul>
          </section>
        ))}
        <p className="help-footnote">
          Editor shortcuts are Monaco built-ins; on Windows/Linux <kbd>{mod}</kbd> is <kbd>Ctrl</kbd>.
        </p>
      </div>
    </div>
  );
}
